import { URLS } from '@/lib/consts';
import { type ServiceURLs, ServiceType } from '../../types';

/*
 * Page Data
 */
export const SERVICES_SECTION_ID = 'services';
export const SERVICES_SECTION_SUB_TITLE = 'SERVICES';
export const CTA_BUTTON_TEXT = "What\u2019s Included?";

/*
 * Service Card Type
 */
export interface ServiceCardData {
  name: string;
  tagline: string;
  icon: string;
}

/*
 * Build Services Data
 */
const BUILD_SERVICES_TITLE = 'Custom Structures for Every Lifestyle in Rogers County';
const BUILD_SERVICES_DESC =
  `Whether you are building your forever home on acreage outside Claremore, a working barn for your Rogers County property, or a custom shop for your workshop needs, Cornerstone Homes designs and builds it from the ground up. Explore our design and build services below and find the right fit for your vision.`;

export const BuildServiceCards: ServiceCardData[] = [
  {
    name: 'Home',
    tagline: 'Your dream home, designed & built from scratch',
    icon: 'Home',
  },
  {
    name: 'Cottage',
    tagline: 'Charming retreats & guest houses',
    icon: 'TreePine',
  },
  {
    name: 'Barn',
    tagline: 'Purpose-built barns for horses, livestock & equipment',
    icon: 'Warehouse',
  },
  {
    name: 'Shop',
    tagline: 'Custom workshops & commercial workspaces',
    icon: 'Wrench',
  },

  {
    name: 'Bunkhouse',
    tagline: 'Guest quarters, rentals & retreats',
    icon: 'BedDouble',
  },
];

/*
 * Renovate Services Data
 */
const RENOVATE_SERVICES_TITLE = "What\u2019s on Your Renovation Wishlist?";
const RENOVATE_SERVICES_DESC =
  `Whether it\u2019s updating your kitchen, finishing your basement, or adding an entirely new room, Cornerstone Homes offers comprehensive renovation services across Claremore and the Tulsa metro area. Our experienced team handles every detail so your renovation stays on track and on budget.`;

export const RenovateServiceCards: ServiceCardData[] = [
  {
    name: 'Kitchen',
    tagline: 'Modern upgrades & complete remodels',
    icon: 'ChefHat',
  },
  {
    name: 'Bathroom',
    tagline: 'Beautiful, functional bath remodels',
    icon: 'Bath',
  },
  {
    name: 'Basement',
    tagline: 'Unlock your basement\u2019s full potential',
    icon: 'ArrowDownToLine',
  },
  {
    name: 'Addition',
    tagline: 'Expand your living space seamlessly',
    icon: 'PlusSquare',
  },
  {
    name: 'Garage',
    tagline: 'Organized storage & workspace conversions',
    icon: 'Car',
  },
  {
    name: 'Screened-In-Porch',
    tagline: 'Outdoor living, year-round comfort',
    icon: 'Fence',
  },
  {
    name: 'Boat-House',
    tagline: 'Waterfront storage & recreation',
    icon: 'Ship',
  },
  {
    name: 'Bunkhouse',
    tagline: 'Revitalize guest quarters & retreats',
    icon: 'BedDouble',
  },
];

/*
 * Type Definitions
 */
export interface ServiceSectionData {
  title: string;
  description: string;
  services: ServiceCardData[];
}

/*
 * Generates Services Section Data Based on Service Type
 */
function generateServiceSectionData(
  serviceType: ServiceType,
): ServiceSectionData {
  switch (serviceType) {
    case ServiceType.BUILD: {
      return {
        title: BUILD_SERVICES_TITLE,
        description: BUILD_SERVICES_DESC,
        services: BuildServiceCards,
      };
    }

    case ServiceType.RENOVATE: {
      return {
        title: RENOVATE_SERVICES_TITLE,
        description: RENOVATE_SERVICES_DESC,
        services: RenovateServiceCards,
      };
    }

    case ServiceType.ROOT: {
      return { title: '', description: '', services: [] };
    }

    default: {
      throw new Error(
        'ServiceType provided does not exist for Service Section',
      );
    }
  }
}

/*
 * Services Section Data Mapped to URLs
 */
const ROOT_SERVICE_SECTION_DATA = generateServiceSectionData(ServiceType.ROOT);
const BUILD_SERVICE_SECTION_DATA = generateServiceSectionData(
  ServiceType.BUILD,
);
const RENOVATE_SERVICE_SECTION_DATA = generateServiceSectionData(
  ServiceType.RENOVATE,
);

const SERVICES_SECTION_CONTENT: Record<ServiceURLs, ServiceSectionData> = {
  [URLS.SERVICES.ROOT]: ROOT_SERVICE_SECTION_DATA,
  [URLS.SERVICES.DESIGN_AND_BUILD]: BUILD_SERVICE_SECTION_DATA,
  [URLS.SERVICES.DESIGN_AND_RENOVATE]: RENOVATE_SERVICE_SECTION_DATA,
};

/*
 * Simple name lists for backward compatibility (used by sitemap.ts)
 */
export const BuildServicesList = BuildServiceCards.map((s) => s.name) as readonly string[];
export const RenovateServicesList = RenovateServiceCards.map((s) => s.name) as readonly string[];

export default SERVICES_SECTION_CONTENT;
